import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Eyebrow, Heading, ShelfHead, Text } from '../ui/index.js';
import { useAccess, useGames, useLibraryUpdates } from '../api/hooks.js';
import { useInstalledGames } from '../components/game/useInstallState.js';
import { FocusZone } from '../components/focus/Focusable.js';
import { GameCard } from '../components/game/GameCard.js';
import { FeaturedHero } from '../components/home/FeaturedHero.js';
import { PlanTeaser } from '../components/home/PlanTeaser.js';
import { SpotlightAction } from '../components/home/SpotlightAction.js';
import { WhatThisIs } from '../components/home/WhatThisIs.js';
import {
  FRESH_DAYS,
  homeAudience,
  homeLayout,
  homeNews,
  homeShelf,
  homeShelves,
  homeSpotlight,
  shelfNote,
  spotlightEyebrow,
} from '../components/home/homeView.js';
import { useLastPlayed } from '../library/recent.js';
import { SupporterCta } from '../components/layout/SupporterCta.js';
import { Loading } from '../components/layout/Loading.js';

export const Route = createFileRoute('/')({
  component: Home,
  validateSearch: (search: Record<string, unknown>): { a?: string } =>
    typeof search.a === 'string' ? { a: search.a } : {},
});

/**
 * The front door, and a different door depending on who opens it (`03-client-app.md` section 4.1).
 *
 * It used to be one screen for everybody, with the supporter slab bolted onto the bottom for
 * whoever had not paid. That is the right instinct for a console — the shelf is the shelf — and
 * the wrong one for a front door, because the three people who open it are not asking the same
 * question. `homeView.ts` names the three; this screen is what each of them gets.
 *
 * - **A visitor** gets the argument. Artwork, then what the studio is and what the membership
 *   opens, then what it costs, then the catalogue. A Play button they cannot press is not an
 *   argument, and the price is the one thing they came to find out, so neither is left to a
 *   screen they have to navigate to first.
 * - **An account with no membership** gets the ask, and nothing dressed up as anything else.
 *   They have already been told what this is — they made an account — so the explainer comes
 *   off and the slab moves up to sit directly under the artwork. Below it: the builds pushed
 *   this fortnight that they cannot install, which is the argument stated as fact rather than
 *   as copy, then the prices, then the shelf.
 * - **A supporter** gets the news: one game held up with the thing to do to it on the button,
 *   then every game on one shelf with the reason under each. No ask anywhere — the one thing
 *   worse than failing to sell is selling to somebody who has already bought.
 *
 * The supporter half used to be four headed rows: Updates ready, New builds, Continue playing,
 * and the catalogue underneath. Rows are how you keep a large catalogue readable, and this is
 * not one yet. A game pushed on Tuesday and installed here appeared under New builds, under
 * Continue playing and again in the catalogue, with a five-slide carousel over the top cycling
 * most of the same shelf — four tiles, one game, and a screen that looked padded rather than
 * full. So below `ROWS_FROM` games there is one shelf with each game on it once, ordered by
 * what most deserves attention, and the thing a row heading used to say is written under the
 * tile it is true of. The rows come back on their own once there are enough games to need them
 * (`homeView.ts`), and the visitor and the account never had them in the first place.
 */
function Home() {
  const { signedIn, hasAccess, loading: accessLoading } = useAccess();
  const games = useGames();
  const { games: installed } = useInstalledGames();
  const lastPlayed = useLastPlayed();
  const updates = useLibraryUpdates(
    installed.map((g) => ({ gameId: g.gameId, buildId: g.buildId })),
  );

  const all = React.useMemo(() => games.data ?? [], [games.data]);
  const installedIds = React.useMemo(() => new Set(installed.map((g) => g.gameId)), [installed]);

  /**
   * The shelf, the rows, and the clock all three were read against, worked out once.
   *
   * "Pushed in the last fortnight" and "played three days ago" are both comparisons against the
   * clock, and a clock read during render is a value that changes underneath React. Neither has
   * to be right to better than a day, so the reading is taken here, handed to everything that
   * needs it, and left alone until one of these moves.
   *
   * Both shapes are worked out whichever one is drawn. They are a map and a few passes over a
   * catalogue that is small enough for this to be the question in the first place, and the
   * alternative is a memo that has to know the audience before it can decide what to compute.
   */
  const home = React.useMemo(() => {
    const now = Date.now();
    const input = {
      games: all,
      installedIds,
      updateIds: new Set((updates.data?.updates ?? []).map((u) => u.gameId)),
      lastPlayed,
      now,
    };
    return { now, shelves: homeShelves(input), shelf: homeShelf(input) };
  }, [all, installedIds, updates.data, lastPlayed]);

  const shelves = home.shelves;

  // Access is not known on the first paint, and this screen is three screens depending on the
  // answer. A supporter must not watch the sales pitch blink past on the way to their own
  // shelf, and a visitor must not be shown a row of Play buttons that then take themselves back.
  if (games.isLoading || accessLoading) return <Loading label="Opening the workshop" />;

  const audience = homeAudience(signedIn, hasAccess);
  const titles = `${all.length} ${all.length === 1 ? 'title' : 'titles'}`;

  /** Rows or one shelf, decided by how much catalogue there is to divide. */
  const layout = homeLayout(all.length);

  /**
   * The one game a supporter's hero holds, and null for everybody else.
   *
   * It is the head of the shelf: already sorted by what most deserves attention, so the first
   * tile on it is also the right thing to put at room scale. A visitor and an unpaid account
   * keep the five-slide carousel, because for them the hero is a shop window and cycling the
   * catalogue is the point of it.
   */
  const spotlight = audience === 'supporter' ? homeSpotlight(home.shelf) : null;

  /** The whole shelf, which every audience gets and only the note underneath differs on. */
  const catalogue =
    all.length > 0 ? (
      <Rack
        title="Games"
        note={
          audience === 'supporter' ? `${titles}, all included` : `${titles}, all in the membership`
        }
        focusKey="home-catalog"
      >
        {all.map((game) => (
          <GameCard key={game.id} game={game} installed={installedIds.has(game.id)} shape="wide" />
        ))}
      </Rack>
    ) : null;

  return (
    <FocusZone focusKey="home" direction="column" className="gb-stack gb-stack--loose">
      {spotlight ? (
        <FeaturedHero
          games={[spotlight.game]}
          hasAccess
          eyebrow={spotlightEyebrow(spotlight, home.now)}
          action={<SpotlightAction game={spotlight.game} />}
        />
      ) : all.length > 0 ? (
        <FeaturedHero games={all} hasAccess={hasAccess} />
      ) : (
        <Card size="4" className="gb-center" style={{ minHeight: '16rem' }}>
          <Eyebrow tone="lemon">{'Nothing published yet'}</Eyebrow>
          <Heading size="6">{'The first build lands here.'}</Heading>
          <Text color="gray">
            {'When we push something playable, this is where it shows up first.'}
          </Text>
        </Card>
      )}

      {/* ----------------------------------------------------------- a visitor */}

      {audience === 'visitor' ? (
        <>
          <WhatThisIs />
          <PlanTeaser />
          {catalogue}
        </>
      ) : null}

      {/* ------------------------------------- an account, with no membership on it */}

      {audience === 'account' ? (
        <>
          {/* The whole screen is this: first thing under the artwork, before anything they
              could mistake for a way in. No `autoFocus` on it, though — the hero above already
              took the ring, and two elements calling for it on one screen is a race whose
              winner scrolls the page past the thing it was opened on. */}
          <SupporterCta
            title="You have the account. This is the part that opens it."
            body="Every game, every dev build and every prototype, while we make them. Cancel any time."
            gameCount={all.length}
          />

          {/* The pitch, stated as fact. A fortnight of real builds says more about a studio
              working in the open than a sentence claiming to, and every one of them is
              something the membership would already have installed. */}
          {shelves.fresh.length > 0 ? (
            <Rack
              title="Pushed this fortnight"
              note={`${shelves.fresh.length} ${
                shelves.fresh.length === 1 ? 'build' : 'builds'
              } in the last ${FRESH_DAYS} days`}
              focusKey="home-fresh"
            >
              {shelves.fresh.map((game) => (
                <GameCard key={game.id} game={game} shape="wide" />
              ))}
            </Rack>
          ) : null}

          <PlanTeaser
            title="What it costs."
            body="Prices include VAT, so what you see is what you pay. Cancel any time."
          />

          {catalogue}
        </>
      ) : null}

      {/* --------------------------------------------------------- a supporter */}

      {audience === 'supporter' && layout === 'shelf' ? (
        /* One shelf, every game on it once, and the reason under each.

           The heading carries what the row headings used to: "2 updates ready · 1 new build ·
           6 games" is the same news three headings were spelling out, and it costs six tiles
           fewer. Underneath, the order is the argument — updates first, then this fortnight's
           builds, then what is installed, then the rest — and the note on each tile is what
           makes that order legible rather than mysterious. */
        <Rack title="Your games" note={homeNews(home.shelf)} focusKey="home-shelf">
          {home.shelf.map((item) => (
            <GameCard
              key={item.game.id}
              game={item.game}
              installed={installedIds.has(item.game.id)}
              shape="wide"
              caption="state"
              note={shelfNote(item, home.now)}
            />
          ))}
        </Rack>
      ) : null}

      {audience === 'supporter' && layout === 'rows' ? (
        <>
          {shelves.needUpdate.length > 0 ? (
            <Rack
              title="Updates ready"
              note="A newer build than the one on this machine"
              focusKey="home-updates"
            >
              {shelves.needUpdate.map((game) => (
                <GameCard key={game.id} game={game} installed updateAvailable shape="wide" />
              ))}
            </Rack>
          ) : null}

          {/* What has happened since they last looked, minus whatever is already waiting on this
              machine — that is the same news, said better, one row up. In a browser it is the
              only news row there is: nothing is installed there, so nothing can be behind. */}
          {shelves.fresh.length > 0 ? (
            <Rack
              title="New builds"
              note={`Pushed in the last ${FRESH_DAYS} days`}
              focusKey="home-fresh"
            >
              {shelves.fresh.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  installed={installedIds.has(game.id)}
                  shape="wide"
                />
              ))}
            </Rack>
          ) : null}

          {/* A game already installed is a game already recognised, so this row does not need the
              portrait capsule to identify it. The board's landscape tile gives the build tag room
              to sit beside the title instead of under it. */}
          {shelves.continuePlaying.length > 0 ? (
            <Rack title="Continue playing" focusKey="home-continue">
              {shelves.continuePlaying.map((game) => (
                <GameCard key={game.id} game={game} installed shape="wide" />
              ))}
            </Rack>
          ) : null}

          {catalogue}
        </>
      ) : null}
    </FocusZone>
  );
}

/**
 * A rack: a mono heading, then four games across, wrapping onto as many rows as it takes.
 *
 * It used to be a shelf that ran off the right edge, and a shelf is a mouse idea. A wheel and a
 * finger both drag one cheaply; a stick does not. Twenty games on one row is twenty presses of
 * right with the artwork sliding under the ring the whole way, and no sense of how far along
 * you are. Four columns that wrap gives a controller a real grid — down is the row underneath,
 * not a guess at whatever is geometrically nearest — and gives the eye a column to scan.
 *
 * Four is the board's own rhythm, and it is also about as many landscape tiles as stay legible
 * across a 1600px page. Narrower windows step down rather than shrinking the artwork.
 */
function Rack({
  title,
  note,
  focusKey,
  children,
}: {
  title: string;
  note?: string;
  focusKey: string;
  children: React.ReactNode;
}) {
  return (
    <section className="gb-section">
      <ShelfHead title={title} note={note} />
      <FocusZone focusKey={focusKey} direction="grid" className="gb-rack">
        {children}
      </FocusZone>
    </section>
  );
}
